import EditorSection from "../../components/EditorSection";
import { resolveImageUrl } from "../../utils/ImageUrl";
import ProductUnitSetup from "../../components/InventoryLines/ProductUnitSetup";
import { defaultProductUnit, sellingPrice } from "../../utils/ProductUnits";
import { useAuth } from "../../auth/useAuth";
import { Button, Dropdown, FileUpload, Input, MultiSelect, VStack, Switch, Notification, } from "@akin2unde/flowui-react";
import type { DropdownModel } from "@akin2unde/flowui-react";
import type { ReactNode } from "react";
import RightSideDialogHTML from "../../components/RightSideDialog/RightSideDialogHTML";
import { useProduct } from "./Product";
import type { ProductStatusFilter } from "./ProductState";
import "./ProductCss.css";
import { DataState } from "../../models/DataState";
const statuses: Array<{
    Label: string;
    Value: ProductStatusFilter;
}> = [
    { Label: "All products", Value: "All" },
    { Label: "Active", Value: "Active" },
    { Label: "Low stock", Value: "LowStock" },
    { Label: "Out of stock", Value: "OutOfStock" },
];
const currency = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
});
export default function ProductHTML() {
    const { hasPermission, state: authState } = useAuth();
    const allowed = (action: string) => hasPermission(`Product.${action}`) ||
        (authState.RoleUis === undefined && hasPermission("Product.Manage"));
    const { reload, state, units, unitError, filteredProducts, summary, setStateProperty, changeFilter, changeStatus, openCreateDialog, openEditDialog, closeDialog, setProductProperty, saveProduct, addProductImages, removeProductImage, setPrimaryProductImage, replaceProductImage, removeProduct, previousPage, nextPage, } = useProduct();
    const { products, categories } = state;
    return (<section className="product-page">
      <header className="product-heading">
        <div>
          <p className="product-eyebrow">Inventory catalogue</p>
          <h1>Products</h1>
          <p>Manage products, prices and stock availability.</p>
        </div>
        <div className="product-heading-actions">

          <Button disabled={!allowed("Create")} onClick={openCreateDialog}>
            <i className="fa-solid fa-plus" aria-hidden="true"/>
            Add product
          </Button>
        </div>
      </header>

      <div className="product-summary">
        <SummaryCard label="Total products" value={state.loading || state.loadError ? "—" : summary.Total} icon="fa-boxes-stacked" tone="green"/>
        <SummaryCard label="Active" value={state.loading || state.loadError ? "—" : summary.Active} icon="fa-circle-check" tone="blue"/>
        <SummaryCard label="Low stock" value={state.loading || state.loadError ? "—" : summary.LowStock} icon="fa-triangle-exclamation" tone="orange"/>
        <SummaryCard label="Out of stock" value={state.loading || state.loadError ? "—" : summary.OutOfStock} icon="fa-circle-xmark" tone="red"/>
      </div>

      <article className="product-card">
        <div className="product-toolbar">
          <div className="product-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"/>
            <Input value={state.searchText} placeholder="Search by product name or code" onChange={(event) => setStateProperty("searchText", event.target.value)}/>
          </div>

          <div className="product-filters">
            <label>
              <span>Category</span>
              <Dropdown searchable className="product-category-filter" value={state.category} options={categoryOptions([
            { Key: "All", Value: "All categories" },
            ...categories,
        ])} onChange={(option) => changeFilter("category", String(option.value))}/>
            </label>
            <button className="product-filter-button" type="button" aria-label="More filters">
              <i className="fa-solid fa-sliders" aria-hidden="true"/>
            </button>
          </div>
        </div>

        <div className="product-status-tabs" aria-label="Product status filter">
          {statuses.map((status) => (<button type="button" key={status.Value} onClick={() => changeStatus(status.Value)} className={state.status === status.Value ? "product-status-tab-active" : ""}>
              {status.Label}
            </button>))}
        </div>

        <div className="product-table-wrap">
          <table className="product-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product</th>
                <th>Category</th>
                <th>Selling price</th>
                <th>Stock</th>
                <th>Status</th>
                <th aria-label="Actions"/>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (<tr key={product.Code}>
                  <td>
                    <ProductImageCell Product={product}/>
                  </td>
                  <td>
                    <VStack className="product-identity">
                      <strong>{product.Name}</strong>
                      <small>
                        {product.Code} · {units.find(u => u.Code === defaultProductUnit(product)?.UnitCode)?.Name || defaultProductUnit(product)?.UnitCode || "Units not configured"}
                      </small>
                    </VStack>
                  </td>
                  <td>
                    {(product.CategoryData ?? [])
                .map((item) => String(item.Value ?? ""))
                .join(", ")}
                  </td>
                  <td>
                    <strong>{currency.format(sellingPrice(defaultProductUnit(product), product))}</strong>
                  </td>
                  <td>
                    <div className="product-stock">
                      <strong>
                        {product.TrackInventory ? "Tracked" : "Not tracked"}
                      </strong>
                      <small>Reorder at {product.ReorderLevel ?? 0}</small>
                    </div>
                  </td>
                  <td>
                    <span className={`product-status product-status-${product.Status?.toLowerCase()}`}>
                      {formatStatus(product.Status)}
                    </span>
                  </td>
                  <td>
                    <div className="product-row-actions">
                      <button className="product-action" type="button" style={{
                display: allowed("Edit") ? undefined : "none",
            }} aria-label={`Edit ${product.Name}`} onClick={() => openEditDialog(product)}>
                        <i className="fa-solid fa-pen" aria-hidden="true"/>
                      </button>
                      <button className="product-action product-action-delete" type="button" style={{
                display: allowed("Delete") ? undefined : "none",
            }} aria-label={`Delete ${product.Name}`} onClick={() => {
                if (window.confirm(`Delete ${product.Name}?`))
                    void removeProduct(product);
            }}>
                        <i className="fa-solid fa-trash" aria-hidden="true"/>
                      </button>
                    </div>
                  </td>
                </tr>))}
            </tbody>
          </table>

          {state.loading && <div className="product-empty" role="status">Loading products…</div>}
          {state.loadError && <div className="product-empty" role="alert">{state.loadError}<Button onClick={() => void reload()}>Retry</Button></div>}
          {!state.loading && !state.loadError && filteredProducts.length === 0 && (<div className="product-empty">
              <i className="fa-solid fa-box-open" aria-hidden="true"/>
              <strong>No products found</strong>
              <span>Change your search or filter and try again.</span>
            </div>)}
        </div>

        <footer className="product-pagination">
          <span>
            Showing {filteredProducts.length} of {summary.Total} products
          </span>
          <div>
            <button type="button" disabled={state.skip === 0} onClick={() => void previousPage()}>
              <i className="fa-solid fa-chevron-left" aria-hidden="true"/>
            </button>
            <button type="button" className="product-page-active">
              1
            </button>
            <button type="button" disabled={state.skip + state.limit >= state.total} onClick={() => void nextPage()}>
              <i className="fa-solid fa-chevron-right" aria-hidden="true"/>
            </button>
          </div>
        </footer>
      </article>

      <RightSideDialogHTML Wide IsOpen={state.isDialogOpen} Title={`${state.dialogMode} product`} Description={state.dialogMode === "Create"
            ? "Enter the product, pricing and stock information."
            : "Update the selected product information."} SaveLabel={state.dialogMode === "Create" ? "Create product" : "Save changes"} OnClose={closeDialog} OnSave={saveProduct} Children={<div className="editor-layout product-editor"><EditorSection className="editor-section" title={<>Product details</>}><div className="right-dialog-form">
            <ProductField Label="Product code (optional)">
              <Input disabled placeholder="Generated automatically by the backend" value={state.selectedProduct.Code} onChange={(event) => setProductProperty("Code", event.target.value)}/>
            </ProductField>
            <ProductField Label="Product name" Required>
              <Input value={state.selectedProduct.Name} onChange={(event) => setProductProperty("Name", event.target.value)} required/>
            </ProductField>

            <ProductField Label="Categories" Required IsFullWidth={true}>
              <MultiSelect className="product-category-select" chipTextColor="primary.700" chipBackgroundColor="primary.100" value={state.selectedProduct.Categories} options={categoryOptions(categories.filter((category) => category.Key !== "All"))} placeholder="Select categories" searchable showCheckboxes onChange={(values) => setProductProperty("Categories", values.map(String))}/>
              <small>Select every category that applies to this product.</small>
            </ProductField>
            <ProductField Label="Base stock unit" Required>
              <Dropdown disabled={state.dialogMode === "Edit"} value={state.selectedProduct.BaseUnitCode} placeholder="Select base stock unit" options={units.filter(x => x.IsActive || x.Code === state.selectedProduct.BaseUnitCode).map(x => ({ display: x.Name, value: x.Code, other: x }))} onChange={option => setProductProperty("BaseUnitCode", String(option.value))}/>
              {state.dialogMode === "Edit" && <small>Base stock unit is fixed after creation. Existing conversions are retained to protect stock and transaction history.</small>}
              <small>Inventory quantities and cost price use this unit. Keep it unchanged once stock exists.</small>
            </ProductField>
            </div></EditorSection><EditorSection className="editor-section editor-products" title={<>Units and pricing</>}><div className="right-dialog-form">
            <ProductUnitSetup product={state.selectedProduct} units={units} lockedUnitCodes={state.dialogMode === "Edit" ? state.products.find(p => p.Code === state.selectedProduct.Code)?.UnitPrices?.map(u => u.UnitCode) ?? [] : []} onChange={rows => setProductProperty("UnitPrices", rows)}/>
            {unitError && <p className="right-dialog-field-full" role="alert">{unitError}</p>}
            </div></EditorSection><EditorSection className="editor-section" title={<>Additional details</>}><div className="right-dialog-form">
            <ProductField Label="Barcode">
              <Input value={state.selectedProduct.Barcode ?? ""} onChange={(event) => setProductProperty("Barcode", event.target.value)}/>
            </ProductField>
            <ProductField Label="Description" IsFullWidth>
              <Input value={state.selectedProduct.Description ?? ""} onChange={(event) => setProductProperty("Description", event.target.value)}/>
            </ProductField>
            </div></EditorSection><EditorSection className="editor-section editor-side" title={<>Stock and visibility</>}><div className="right-dialog-form">
            <ProductField Label="Cost price">
              <Input type="number" inputMode="money" currencySymbol="₦" step={0.01} value={state.selectedProduct.CostPrice ?? 0} readOnly disabled/>
            </ProductField>
            <ProductField Label="Reorder level">
              <Input type="number" inputMode="decimal" step="any" value={state.selectedProduct.ReorderLevel ?? 0} onChange={(event) => setProductProperty("ReorderLevel", Number(event.target.value))}/>
            </ProductField>
            <ProductField Label="Track inventory">
              <Switch checked={state.selectedProduct.TrackInventory} onChange={(value) => setProductProperty("TrackInventory", value)}/>
            </ProductField>
            <ProductField Label="Allow out-of-stock orders">
              <Switch checked={state.selectedProduct.AllowOutOfStockOrder} onChange={(value) => setProductProperty("AllowOutOfStockOrder", value)}/>
            </ProductField>
            <ProductField Label="Allow credit sale">
              <Switch checked={state.selectedProduct.AllowCreditSale} onChange={(value) => setProductProperty("AllowCreditSale", value)}/>
            </ProductField>
            <ProductField Label="Visible on storefront">
              <Switch checked={state.selectedProduct.IsVisibleOnStorefront} onChange={(value) => setProductProperty("IsVisibleOnStorefront", value)}/>
            </ProductField>
            <ProductField Label="Status" Required>
              <Dropdown value={state.selectedProduct.Status} options={statusOptions} onChange={(option) => setProductProperty("Status", String(option.value) as typeof state.selectedProduct.Status)}/>
            </ProductField>
            </div></EditorSection><EditorSection className="editor-section" title={<>Images</>}><div className="right-dialog-form">
            <div className="right-dialog-field right-dialog-field-full product-images-editor">
              <label>Product images</label>
              <FileUpload accept="image/*" multiple preview maxSize={10 * 1024 * 1024} onChange={(files) => void addProductImages(files)}/>
              <small>
                Images are converted and capped at 100 KB before upload.
              </small>
              {state.selectedProduct.Images.length > 0 && (<div className="product-image-list">
                  {state.selectedProduct.Images.map((image, index) => (<article key={`${image.Url}-${index}`}>
                      {image.Url ? (<img src={resolveImageUrl(image.Url)} alt={image.ImageFile?.name ??
                            `Product image ${index + 1}`}/>) : (<span>
                          <i className="fa-solid fa-image"/>
                        </span>)}
                      <div>
                        <strong>
                          {image.ImageFile?.name ?? `Image ${index + 1}`}
                        </strong>
                        {image.IsPrimary && <small>Primary image</small>}
                      </div>
                      <label className="product-image-replace">
                        Replace
                        <input type="file" accept="image/*" onChange={(event) => void replaceProductImage(index, event.target.files?.[0])}/>
                      </label>
                      <Button type="button" variant="ghost" onClick={() => setPrimaryProductImage(index)} disabled={image.IsPrimary}>
                        Primary
                      </Button>
                      <Button type="button" variant="ghost" color="danger" onClick={() => removeProductImage(index)}>
                        Remove
                      </Button>
                    </article>))}
                </div>)}
            </div>
          </div></EditorSection></div>}/>
      <Notification open={Boolean(state.successMessage)} type="success" title="Success" duration={3500} onClose={() => setStateProperty("successMessage", "")}>
        <span className="success-message-text">{state.successMessage}</span>
      </Notification>
    </section>);
}
interface ProductFieldProps {
    Label: string;
    Required?: boolean;
    children: ReactNode;
    IsFullWidth?: boolean;
}
function ProductField({ Label, Required, children, IsFullWidth: full = false, }: ProductFieldProps) {
    return (<div className={` ${full ? "right-dialog-field-full" : "right-dialog-field"}`}>
      <label>
        {Label}
        {Required && <em>*</em>}
      </label>

      {children}
    </div>);
}
interface SummaryCardProps {
    label: string;
    value: number | string;
    icon: string;
    tone: string;
}
function SummaryCard({ label, value, icon, tone }: SummaryCardProps) {
    return (<article className="product-summary-card">
      <span className={`product-summary-icon product-tone-${tone}`}>
        <i className={`fa-solid ${icon}`} aria-hidden="true"/>
      </span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </article>);
}
function formatStatus(status: string): string {
    return status.replace(/([a-z])([A-Z])/g, "$1 $2");
}
const statusOptions: DropdownModel[] = ["Active", "LowStock", "OutOfStock"].map((value) => ({
    display: formatStatus(value),
    value,
    other: null,
}));
function categoryOptions(categories: Array<{
    Key: string;
    Value?: unknown;
}>): DropdownModel[] {
    return categories.map((category) => ({
        display: String(category.Value ?? category.Key),
        value: category.Key,
        other: category,
    }));
}
function ProductImageCell({ Product, }: {
    Product: import("../../models/Product").Product;
}) {
    const availableImages = Product.Images ?? [];
    const primaryImage = availableImages.find((image) => image.IsPrimary) ?? availableImages[0];
    return primaryImage?.Url ? (<img className="product-primary-image" src={resolveImageUrl(primaryImage.Url)} alt={`${Product.Name} primary`}/>) : (<span className="product-primary-image product-primary-image-empty">
      <i className="fa-solid fa-image"/>
    </span>);
}
